import apiClient from "./client";
import type {
  ProjectTargetConnection,
  UpdateTargetConnectionRequest,
  TargetCredentialsRequest,
  TargetTestResult,
  ClientRole,
} from "@/types/api";

/** Conexão do ambiente alvo (GROW) do projeto — S-043.
 * GET devolve o alvo configurado ou `{ configured: false }`. */
export async function getTargetConnection(
  projectId: string,
  clientRole: ClientRole,
): Promise<ProjectTargetConnection> {
  const res = await apiClient.get<ProjectTargetConnection>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection`,
    { params: { client_role: clientRole } },
  );
  return res.data;
}

export async function updateTargetConnection(
  projectId: string,
  clientRole: ClientRole,
  data: UpdateTargetConnectionRequest,
): Promise<ProjectTargetConnection> {
  const res = await apiClient.put<ProjectTargetConnection>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection`,
    { ...data, client_role: clientRole },
  );
  return res.data;
}

/** Grava (cifradas no servidor) as credenciais OData do target. */
export async function updateTargetCredentials(
  projectId: string,
  data: TargetCredentialsRequest,
): Promise<{ saved: boolean; has_credentials: boolean }> {
  const res = await apiClient.put<{ saved: boolean; has_credentials: boolean }>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection/credentials`,
    data,
  );
  return res.data;
}

/** Testa o alvo. development: OData ao vivo; configuration: virtualhost + cliente 100. */
export async function testTargetConnection(
  projectId: string,
  clientRole: ClientRole,
): Promise<TargetTestResult> {
  const res = await apiClient.post<TargetTestResult>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection/test`,
    undefined,
    { params: { client_role: clientRole } },
  );
  return res.data;
}

// --- Sessão ADT do modo B, por usuário e por projeto (S-048) ----------------

/** Status da sessão ADT do dev logado: connected / expired / disconnected. */
export interface AdtSessionStatus {
  status: "connected" | "expired" | "disconnected";
  sap_responsible?: string | null;
  connected_at?: string | null;
  expires_at?: string | null;
}

/**
 * Inicia o SSO ADT (reentrance): devolve a URL pro dev abrir no browser.
 *
 * `callback` decide para onde o SAP devolve o ticket. `direct` cai no callback deste
 * mesmo host, e só funciona com o myGROW rodando na máquina do dev. `localhost` pede o
 * retorno em loopback, que é o único que o SAP aceita, e é o que a extensão
 * adt-connect intercepta. Ver S-070.
 */
export async function startAdtLogon(
  projectId: string,
  callback: "direct" | "localhost" = "direct",
): Promise<{ reentrance_url: string }> {
  const res = await apiClient.get<{ reentrance_url: string }>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection/adt-logon`,
    { params: { callback } },
  );
  return res.data;
}

export interface AdtConnectResult {
  ok: boolean;
  responsible: string;
  expires_at: string;
}

/**
 * Conecta a sessão ADT com um ticket já recebido, em vez de esperar o redirect.
 *
 * Aceita o ticket puro ou a URL de retorno inteira. Quem autentica é o `state`
 * assinado, então esta chamada funciona mesmo sem sessão do myGROW.
 */
export async function connectAdtWithTicket(
  projectId: string,
  body: { state: string; ticket?: string; callback_url?: string },
): Promise<AdtConnectResult> {
  const res = await apiClient.post<AdtConnectResult>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection/adt-session`,
    body,
  );
  return res.data;
}

/** Status da sessão ADT do dev logado (pro card e pro gate de escrita). */
export async function getAdtStatus(
  projectId: string,
): Promise<AdtSessionStatus> {
  const res = await apiClient.get<AdtSessionStatus>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection/adt-status`,
  );
  return res.data;
}

/** Desconecta a sessão ADT do dev (o "sair"). */
export async function disconnectAdt(
  projectId: string,
): Promise<AdtSessionStatus> {
  const res = await apiClient.delete<AdtSessionStatus>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection/adt-session`,
  );
  return res.data;
}

/** Mantém viva a sessão SAP (keepalive) e estende a validade. */
export async function keepAdtAlive(
  projectId: string,
): Promise<AdtSessionStatus> {
  const res = await apiClient.post<AdtSessionStatus>(
    `/api/projects/${encodeURIComponent(projectId)}/target-connection/adt-keepalive`,
  );
  return res.data;
}
